/**
 * NotificationItem Component
 *
 * Single notification item with type-based styling and actions.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Clock,
  Wifi,
  WifiOff,
  Info,
  Trash2,
  Cpu,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Notification,
  NotificationType,
} from '@/services/notificationService';
import { getNotificationStyle } from '@/hooks/useNotifications';
import { getNotificationNavUrl } from '@/services/notificationNav';
import { formatDistanceToNow } from 'date-fns';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

// Icon mapping for notification types
const typeIcons: Record<NotificationType, React.ComponentType<{ className?: string }>> = {
  execution_complete: CheckCircle,
  execution_failed: XCircle,
  // Distinct icon so users immediately see "this is an eSIM problem" vs a
  // flow-logic failure. Set by notificationTriggers.ts when an execution
  // has failureCategory='esim_provisioning'.
  esim_provisioning_failure: Cpu,
  schedule_triggered: Clock,
  device_offline: WifiOff,
  device_online: Wifi,
  system: Info,
};

export function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  onClose,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const { color, bgColor } = getNotificationStyle(notification.type);
  const Icon = typeIcons[notification.type];

  const handleClick = () => {
    // Mark as read when clicking
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }

    // Navigate if applicable (shared with the toast "View" action)
    const url = getNotificationNavUrl(notification);
    if (url) {
      onClose();
      navigate(url);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  const timeAgo = formatDistanceToNow(notification.createdAt, { addSuffix: true });

  return (
    <div
      onClick={handleClick}
      className={`
        group relative p-3 cursor-pointer transition-colors
        ${notification.read ? 'bg-background' : 'bg-muted/50'}
        hover:bg-muted
      `}
    >
      {/* Unread indicator */}
      {!notification.read && (
        <span className="absolute left-1 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-accent-blue" />
      )}

      <div className="flex gap-3 ml-2">
        {/* Icon */}
        <div className={`flex-shrink-0 w-8 h-8 rounded-full ${bgColor} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-sm font-medium ${notification.read ? 'text-muted-foreground' : 'text-foreground'}`}>
              {notification.title}
            </p>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity -mr-1"
              onClick={handleDelete}
            >
              <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-accent-red" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
            {notification.message}
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            {timeAgo}
          </p>
        </div>
      </div>
    </div>
  );
}
